import * as React from 'react';
import {ColumnProps} from 'antd/lib/table';
import {ChecklistState, ChecklistSummary, RentalItem} from '../api/dtos/dtos';
import locales from '../locales';
import ButtonGroup from 'antd/lib/button/button-group';
import {Button, Modal, Table} from 'antd';
import RentalItemForm from '../rental-details/RentalItemForm';
import RentalItemSelector from './RentalItemSelector';
import {renderStateElementCondensed} from '../common/state';
import {getAmountTypeLabel} from '../common/AmountType';
import {simpleColumn} from '../utils/table-utils';
import {alignRightNoWrap, groupNumber, renderMmColumn, renderPriceColumn} from '../utils/number-utils';
import ButtonBar from '../common/ButtonBar';
import {EditorMode} from '../common/types';
import * as paths from '../paths';
import {Link} from 'react-router-dom';
import {renderItemName} from "../item/item-name-renderer";
import {findChecklistByRentalItemId} from "../checklist/dtos";
import ChecklistStateLabel from "../checklist/ChecklistStateLabel";


interface ReturnItemDetailsEditorProps {
  mode: EditorMode;
  editable: boolean;
  rentalItems: RentalItem[];
  onRentalItemsChange: (rentalItems: RentalItem[]) => void;
  clientId: number | null;
  showChecklist: boolean;
  checklistSummary?: ChecklistSummary;
  onChecklistAdd?: (rentalItemId: number) => void;
  onChecklistEdit?: (checklistId: number) => void;
}

interface ReturnItemDetailsEditorState {
  editedRentalItem?: RentalItem;
  // After edit from is submitted we use this to manipulate the rentalItems list
  editedRentalItemIdx?: number;
  itemSelectorVisible: boolean;
}

class ReturnItemsEditor extends React.Component<ReturnItemDetailsEditorProps, ReturnItemDetailsEditorState> {

  public readonly state: ReturnItemDetailsEditorState = {
    itemSelectorVisible: false,
  };

  public render(): React.ReactNode {

    const columns: Array<ColumnProps<RentalItem>> = [
      {
        ...simpleColumn('', r => renderStateElementCondensed(r.state)),
        key: 'state',
        width: 38,
      },
      simpleColumn(locales.item, r => <Link to={paths.viewItemPath(r.item.id)}>{renderItemName(r.item)}</Link>),
      simpleColumn(locales.inventoryNumber, r => r.item.inventoryNumber),
      {
        ...simpleColumn(locales.caution, r => renderPriceColumn(r.caution)),
        className: 'align-right',
      },
      simpleColumn(locales.comment, r => r.comment),
      {
        ...simpleColumn(locales.operationHours, r => alignRightNoWrap(groupNumber(r.returnalOperationHours))),
        className: 'align-right',
      }, {
        ...simpleColumn(locales.abrasion_dimension, r => renderMmColumn(r.returnalAbrasionMm)),
        className: 'align-right',
      }, {
        ...simpleColumn(locales.net_price, r => renderPriceColumn(r.price)),
        className: 'align-right',
      },
      simpleColumn(locales.settlement, r => getAmountTypeLabel(r.amountType)),
    ];

    if (this.props.showChecklist) {
      columns.push({
        ...simpleColumn(locales.checklist, r => this.renderChecklistColumn(r), 190),
      });
    }

    if (this.props.editable) {
      columns.push({key: 'actions', width: 80, render: this.renderActionColumn});
    }


    const {rentalItems, clientId, editable, mode} = this.props;
    const {editedRentalItem, itemSelectorVisible} = this.state;
    const rowKeyFn = (record: RentalItem, index: number) => String(index);
    const excludedRentalItemsFromSearch: RentalItem[] = rentalItems.map(ri => ri);

    return (
      <>
        <Modal
          title={locales.edit_entry}
          visible={editedRentalItem !== undefined}
          footer={null}
          onCancel={this.onRentalItemFormCancel}
          destroyOnClose={true}
        >
          <RentalItemForm
            rentalItem={editedRentalItem}
            onFormSubmit={this.onRentalItemFormSubmit}
            onFormCancel={this.onRentalItemFormCancel}
          />
        </Modal>

        <Modal
          title={locales.select_item}
          visible={itemSelectorVisible}
          footer={null}
          onCancel={this.onItemSelectorCancel}
          width="auto"
          style={{margin: '0 24px'}}
          destroyOnClose={true}
        >
          <RentalItemSelector
            clientId={clientId}
            excludedFromSearch={excludedRentalItemsFromSearch}
            onSelect={this.onItemSelect}/>
        </Modal>

        {mode === 'create' && editable && <ButtonBar style={{marginBottom: 8}}>
            <Button size="small" type="primary" icon="plus"
                    onClick={this.onAddRentalItemClick}>{locales.add}</Button>
        </ButtonBar>}

        <Table
          style={{marginBottom: 24}}
          columns={columns}
          dataSource={rentalItems}
          rowKey={rowKeyFn}
          bordered={true}
          pagination={false}
          size="small"
          scroll={{x: 1024}}
        />
      </>
    );
  }

  private renderActionColumn = (text: any, record: RentalItem, index: number) => {
    const {editable, mode} = this.props;
    const onEditRentalItemClick = () => this.onEditRentalItemClick(record, index);
    const onDeleteRentalItemClick = () => this.onDeleteRentalItemClick(record, index);
    return editable && (
      <div className="action-column" style={{textAlign: 'right'}}>
        <ButtonGroup>
          <Button icon="edit" onClick={onEditRentalItemClick} title={locales.edit}/>
          {mode === 'create' && (
            <Button icon="delete" onClick={onDeleteRentalItemClick} title={locales.delete}/>
          )}
        </ButtonGroup>
      </div>
    );
  };

  private renderChecklistColumn = (record: RentalItem) => {
    const onAddChecklistClick = () => this.onAddChecklistClick(record);
    const onEditChecklistClick = () => this.onEditChecklistClick(record);
    return (
      <div style={{display: "flex", alignItems: "center"}}>
        {this.checklistMandatory(record) && !this.hasChecklist(record) && (
          <Button icon="plus" onClick={onAddChecklistClick} style={{marginRight: 8, minWidth: 32, width: 32,}}/>

        )}
        {this.hasChecklist(record) && (
          <Button icon="edit" onClick={onEditChecklistClick} style={{marginRight: 8, minWidth: 32, width: 32,}}/>
        )}
        {this.checklistMandatory(record) &&
          this.renderChecklistState(record)
        }
      </div>
    );
  };

  private renderChecklistState = (record: RentalItem): React.ReactNode => {
    const {checklistSummary} = this.props;
    const checklist = findChecklistByRentalItemId(checklistSummary?.checklists, record.id!)
    const state = checklist?.state ?? ChecklistState.MISSING;
    return (
      <ChecklistStateLabel state={state}/>
    );
  }

  private checklistMandatory = (record: RentalItem): boolean => {
    return record.item.checklistRequired ?? false;
  };

  private hasChecklist = (record: RentalItem): boolean => {
    return record.returnalChecklistId !== null;
  };

  private onAddChecklistClick = (rentalItem: RentalItem) => {
    this.props.onChecklistAdd!(rentalItem.id!);
  };

  private onEditChecklistClick = (rentalItem: RentalItem) => {
    this.props.onChecklistEdit!(rentalItem.returnalChecklistId!);
  };


  private onEditRentalItemClick = (rentalItem: RentalItem, index: number) => {
    this.setState({
      editedRentalItem: rentalItem,
      editedRentalItemIdx: index,
    });
  };

  private onDeleteRentalItemClick = (rentalItem: RentalItem, index: number) => {
    const {rentalItems, onRentalItemsChange} = this.props;
    const newItems = [...rentalItems];
    newItems.splice(index, 1);
    onRentalItemsChange(newItems);
  };

  private onRentalItemFormSubmit = (item: RentalItem) => {
    this.onRentalItemChange(item);
  };

  private onRentalItemChange = (item: RentalItem) => {
    const {rentalItems, onRentalItemsChange} = this.props;
    let newItems: RentalItem[];

    const idx = this.state.editedRentalItemIdx;
    if (idx === undefined) {
      // Rental item was created, add it to the beginning of the grid
      newItems = [item, ...rentalItems];
    } else {
      // Rental item was edited, replace it in the grid with the edited one
      newItems = [...rentalItems];
      newItems.splice(idx, 1, item);
    }

    this.setState({
      editedRentalItem: undefined,
      editedRentalItemIdx: undefined,
    }, () => onRentalItemsChange(newItems));
  };

  private onRentalItemFormCancel = () => {
    this.setState({
      editedRentalItem: undefined,
      editedRentalItemIdx: undefined,
    });
  };

  private onAddRentalItemClick = () => {
    this.setItemSelectorVisible(true);
  };

  private onItemSelectorCancel = () => {
    this.setItemSelectorVisible(false);
  };

  private setItemSelectorVisible = (visible: boolean) => {
    this.setState({
      itemSelectorVisible: visible,
    });
  };

  private onItemSelect = (rentalItem: RentalItem) => {
    this.setItemSelectorVisible(false);
    this.addRentalItem(rentalItem);
  };

  private addRentalItem = (rentalItem: RentalItem) => {
    this.onRentalItemChange(rentalItem);
  };
}

export default ReturnItemsEditor;


// WEBPACK FOOTER //
// ./src/return-details/ReturnItemsEditor.tsx